import React, { useEffect, useState, useContext } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Trash2, Star, Edit2, Eye, Copy, X, Search } from 'lucide-react';
import AppShell from '../../components/AppShell';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { AuthContext } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_API_BASE;

export default function MyCreationsDashboard() {
  const [creations, setCreations] = useState([]);
  const [filter, setFilter] = useState({ type: '', favorite: '', start: '', end: '' });
  const [selected, setSelected] = useState(null);
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();

  const fetchCreations = async () => {
    if (!user?.userId) return;
    try {
      const params = new URLSearchParams({ userId: user.userId, ...filter });
      const res = await fetchWithAuth(`${API}/api/content?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setCreations(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching creations:', err);
      setCreations([]);
    }
  };

  useEffect(() => {
    fetchCreations();
  }, []);

  useEffect(() => {
    document.title = t('Content Creator') + ' – Ennoverse';
  }, []);

  const toggleFavorite = async (id, current) => {
    await fetchWithAuth(`${API}/api/content/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isFavorite: !current }),
    });
    fetchCreations();
  };

  const deleteItem = async (id) => {
    await fetchWithAuth(`${API}/api/content/${id}`, { method: 'DELETE' });
    fetchCreations();
  };

  return (
    <AppShell>
      <div className="col-span-12">
        <h1 className="text-2xl font-semibold mb-4">📁 {t('myCreations')}</h1>

        <div className="flex flex-wrap gap-3 mb-6 items-end">
        <select
  value={filter.type}
  onChange={e => setFilter(f => ({ ...f, type: e.target.value }))}
  className="border p-2 rounded"
>
  <option value="">All Content Types</option>
  <option value="blog">Blog Post</option>
  <option value="caption">Social Media Caption</option>
  <option value="linkedin">LinkedIn Post</option>
  <option value="facebook">Facebook Post</option>
  <option value="instagram">Instagram Post</option>
  <option value="tiktok">TikTok Video</option>
  <option value="twitter">Twitter Post</option>
  <option value="youtube">YouTube Thumbnail</option>
  <option value="productdescription">Product Description</option>
  <option value="script">Video Script</option>
  <option value="email">Email</option>
  <option value="ad">Ad Copy</option>
  <option value="banner">Website Banner</option>
  <option value="brochure">Brochure</option>
  <option value="flyer">Flyer</option>
  <option value="infographics">Infographic</option>
  <option value="thumbnail">Video Thumbnail</option>
</select>

          <select value={filter.favorite} onChange={e => setFilter(f => ({ ...f, favorite: e.target.value }))} className="border p-2 rounded">
            <option value="">All</option>
            <option value="true">⭐ Favorites</option>
            <option value="false">Others</option>
          </select>
          <Input type="date" value={filter.start} onChange={e => setFilter(f => ({ ...f, start: e.target.value }))} />
          <Input type="date" value={filter.end} onChange={e => setFilter(f => ({ ...f, end: e.target.value }))} />
          <Button onClick={fetchCreations} className="bg-primary text-white flex gap-2 items-center">
            <Search className="w-4 h-4" /> {t('search')}
          </Button>
        </div>

        {Array.isArray(creations) && creations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {creations.map((item) => (
              <Card key={item._id} className="relative">
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-md">{item.contentType}</h4>
                    <Button size="icon" variant="ghost" onClick={() => toggleFavorite(item._id, item.isFavorite)} title="Toggle Favorite">
                      <Star className={`w-4 h-4 ${item.isFavorite ? 'text-yellow-500 fill-yellow-500' : ''}`} />
                    </Button>
                  </div>

                  <p className="text-sm text-gray-500">{t('tone')}: {item.tone} • {item.language}</p>

                  <div className="border rounded overflow-hidden bg-gray-100 text-center text-sm text-gray-500 py-4">
  {item.imageUrl && item.imageUrl.length > 0 ? (
    <img
      src={Array.isArray(item.imageUrl) ? item.imageUrl[0] : item.imageUrl}
      alt="Generated Visual"
      className="w-full h-auto"
      onError={(e) => {
        e.target.onerror = null;
        e.target.replaceWith(document.createTextNode(t('no_image')));
      }}
    />
  ) : (
    t('no_image')
  )}
</div>


                  <div className="text-sm max-h-[35vh] overflow-y-auto whitespace-pre-wrap text-gray-800 border rounded p-2 bg-gray-50">
                    {item.generatedText?.slice(0, 1000)}
                  </div>

                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{new Date(item.createdAt).toLocaleString()}</span>
                    <div className="flex gap-2">
                      <Button size="icon" variant="ghost" title="View" onClick={() => setSelected(item)}><Eye className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" title="Duplicate"><Copy className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" title="Edit"><Edit2 className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => deleteItem(item._id)} title="Delete">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">{t('noCreationsFound')}.</p>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-center bg-black bg-opacity-50 overflow-auto px-4 pt-24 pb-10">
        <div className="bg-white w-full max-w-4xl rounded shadow-lg p-6 overflow-auto space-y-4 relative">      
            <button onClick={() => setSelected(null)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold">📄 {t('fullContent')}</h2>
            <div className="text-sm text-gray-500">
              <span className="mr-3">{t('type')}: {selected.contentType}</span>
              <span className="mr-3">{t('tone')}: {selected.tone}</span>
              <span>{t('language')}: {selected.language}</span>
            </div>

            {selected.imageUrl && (
              <div className="space-y-2">
                <img src={Array.isArray(selected.imageUrl) ? selected.imageUrl[0] : selected.imageUrl} alt="Generated Visual" className="w-full rounded border" />
                <Button size="sm" onClick={() => {
                  const link = document.createElement('a');
                  link.href = Array.isArray(selected.imageUrl) ? selected.imageUrl[0] : selected.imageUrl;
                  link.download = 'generated-image.jpg';
                  link.click();
                }}>{t('download_image')}</Button>
              </div>
            )}

            <div className="max-h-[75vh] overflow-y-auto whitespace-pre-wrap text-sm border rounded p-4 bg-gray-50">
              {selected.generatedText}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
